import { useNavigate } from 'react-router-dom';
import { articleQuickLinks } from '@/mocks/articles';

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Was ist das OOPBUY Spreadsheet genau?', acceptedAnswer: { '@type': 'Answer', text: 'Das OOPBUY Spreadsheet ist eine umfassende, kuratierte Übersichtstabelle mit tausenden geprüften Produkten aus chinesischen Online-Marktplätzen.' } },
    { '@type': 'Question', name: 'Ist das OOPBUY Spreadsheet kostenlos?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, das OOPBUY Spreadsheet ist vollständig kostenlos nutzbar.' } },
    { '@type': 'Question', name: 'Wie aktuell sind die Daten im Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Die Daten werden kontinuierlich aktualisiert.' } },
    { '@type': 'Question', name: 'Kann ich Produkte direkt über das Spreadsheet kaufen?', acceptedAnswer: { '@type': 'Answer', text: 'Nein, das Spreadsheet ist eine reine Informationsplattform.' } },
    { '@type': 'Question', name: 'Welche Produktkategorien gibt es?', acceptedAnswer: { '@type': 'Answer', text: 'Das Spreadsheet umfasst Kategorien wie Schuhe, Hoodies, T-Shirts, Jacken, Hosen, Accessoires, Taschen und vieles mehr.' } },
    { '@type': 'Question', name: 'Ist das OOPBUY Spreadsheet auch für Anfänger geeignet?', acceptedAnswer: { '@type': 'Answer', text: 'Absolut! Das Spreadsheet wurde speziell so gestaltet, dass auch Einsteiger schnell die gewünschten Produkte finden.' } },
  ],
});

export default function ArticleGuide() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-background-50">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-secondary-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2 no-underline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <span className="text-lg font-bold tracking-tight text-foreground-950 whitespace-nowrap">
                OOPBUY <span className="text-accent-500">Spreadsheet</span>
              </span>
            </a>
            <div className="hidden md:flex items-center gap-4">
              <a href="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                Startseite
              </a>
              <div className="relative group">
                <button className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer">
                  Shopping Guide
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
                <div className="absolute top-full left-0 mt-1 bg-white border border-secondary-200 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 min-w-[220px]">
                  {articleQuickLinks.map((link) => (
                    <button
                      key={link.path}
                      onClick={() => { navigate(link.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                oopbuy spreadsheet
              </a>
            </div>
            <div className="flex md:hidden items-center gap-2">
              <a href="/" className="text-xs text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                Startseite
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                oopbuy spreadsheet
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-6 pb-2">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="Breadcrumb">
          <a href="/" className="hover:text-foreground-800 transition-colors">Startseite</a>
          <span>/</span>
          <span className="text-foreground-800 font-medium">OOPBUY Spreadsheet Guide</span>
        </nav>
      </div>

      <article className="max-w-[1440px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20laptop%20displaying%20colorful%20spreadsheet%20data%20surrounded%20by%20curated%20fashion%20items%20sneakers%20hoodies%20and%20accessories%20on%20warm%20sand%20colored%20desk%2C%20soft%20natural%20morning%20light%20streaming%20through%20window%2C%20clean%20editorial%20photography%2C%20organized%20creative%20workspace&width=1200&height=500&seq=article-guide-hero-v2&orientation=landscape"
              alt="OOPBUY Spreadsheet kompletter Guide 2026"
              className="w-full aspect-[16/7] object-cover object-top"
            />
          </div>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full">Guide</span>
              <span className="text-xs text-foreground-500">Aktualisiert: Juli 2026</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              OOPBUY Spreadsheet Guide 2026 – Der komplette Leitfaden für Einsteiger und Profis
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              In diesem umfassenden Guide erfährst du alles, was du über das OOPBUY Spreadsheet wissen musst. Von der ersten Nutzung bis zu fortgeschrittenen Tipps.
            </p>
          </header>

          <nav className="bg-background-100 rounded-xl p-6 mb-10" aria-label="Inhaltsverzeichnis">
            <h2 className="text-sm font-medium text-foreground-900 mb-3">Inhaltsverzeichnis</h2>
            <ol className="space-y-1.5">
              {['Was ist das OOPBUY Spreadsheet?', 'Warum brauchst du ein OOPBUY Spreadsheet?', 'Die wichtigsten Kategorien', 'So nutzt du das Spreadsheet richtig', 'Spreadsheet vs. andere Plattformen', 'Tipps für den optimalen Einkauf', 'Häufig gestellte Fragen (FAQ)'].map((item, i) => (
                <li key={i}><a href={`#section-${i + 1}`} className="text-sm text-foreground-600 hover:text-accent-500 transition-colors">{i + 1}. {item}</a></li>
              ))}
            </ol>
          </nav>

          <section id="section-1" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">1. Was ist das OOPBUY Spreadsheet?</h2>
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <p>Das <strong>OOPBUY Spreadsheet</strong> ist eine umfassende, ständig aktualisierte Übersichtstabelle, die tausende von Produkten aus chinesischen Online-Marktplätzen wie Taobao, Weidian und 1688 zusammenfasst.</p>
              <p>Im Gegensatz zu herkömmlichen Shopping-Plattformen bietet es eine <strong>kuratierte Auswahl</strong> an beliebten Artikeln, sortiert nach Kategorien wie Sneakers, Streetwear und Accessoires, mit detaillierten Verkäuferinformationen, Preisen und QC-Bildern.</p>
              <p>Besonders wertvoll ist das Spreadsheet für <strong>Einsteiger</strong>, die sich im komplexen Ökosystem chinesischer Shopping-Agenten zurechtfinden müssen. Statt stundenlang zu suchen, findest du hier vorgeprüfte Links und Empfehlungen auf einen Blick. Lies auch unseren <button onClick={() => { navigate('/artikel/oopbuy-anfaenger-tipps'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 hover:text-accent-600 underline cursor-pointer">Anfänger-Guide</button> für einen leichten Einstieg.</p>
            </div>
          </section>

          <section id="section-2" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">2. Warum brauchst du ein OOPBUY Spreadsheet?</h2>
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <p>Der chinesische E-Commerce-Markt ist riesig. Millionen von Verkäufern bieten Produkte an – doch nicht alle sind vertrauenswürdig. Das OOPBUY Spreadsheet löst dieses Problem mit einer <strong>geprüften und kuratierten Liste</strong>.</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Zeitersparnis:</strong> Keine stundenlange Recherche auf chinesischen Plattformen nötig.</li>
                <li>
                  <strong>Sicherheit:</strong> Nur geprüfte Verkäufer mit nachgewiesener Qualität. Siehe unseren{' '}
                  <button onClick={() => { navigate('/artikel/oopbuy-sicher-einkaufen'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 hover:text-accent-600 underline cursor-pointer">Sicherheits-Guide</button>.
                </li>
                <li><strong>Preisvergleich:</strong> Verschiedene Verkäufer für dasselbe Produkt vergleichen.</li>
                <li>
                  <strong>QC-Transparenz:</strong> QC-Bilder helfen, Fehlkäufe zu vermeiden. Mehr dazu im{' '}
                  <button onClick={() => { navigate('/artikel/oopbuy-qc-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 hover:text-accent-600 underline cursor-pointer">QC Guide</button>.
                </li>
                <li><strong>Community-Wissen:</strong> Profitiere von den Erfahrungen tausender anderer Käufer.</li>
              </ul>
            </div>
          </section>

          <section id="section-3" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">3. Die wichtigsten Kategorien im Überblick</h2>
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                {[
                  { name: 'Schuhe', desc: 'Sneakers, Boots von Nike, Jordan, Yeezy und mehr.', link: '/artikel/oopbuy-sneaker-spreadsheet', linkLabel: 'Sneaker Guide →' },
                  { name: 'Hoodies & Pullover', desc: 'Stylische Hoodies von beliebten chinesischen Verkäufern.', link: '/artikel/oopbuy-hoodie-spreadsheet', linkLabel: 'Hoodie Guide →' },
                  { name: 'T-Shirts', desc: 'Hochwertige T-Shirts mit einzigartigen Designs.', link: '/artikel/oopbuy-preisvergleich-tipps', linkLabel: 'Preisvergleich →' },
                  { name: 'Accessoires', desc: 'Uhren, Schmuck, Sonnenbrillen und mehr.', link: '/artikel/oopbuy-sicher-einkaufen', linkLabel: 'Sicher einkaufen →' },
                ].map((item) => (
                  <div key={item.name} className="bg-background-100 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent-600 text-sm" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900">{item.name}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                      <button onClick={() => { navigate(item.link); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-xs text-accent-500 hover:text-accent-600 mt-1 cursor-pointer">{item.linkLabel}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="section-4" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">4. So nutzt du das OOPBUY Spreadsheet richtig</h2>
            <div className="space-y-4">
              <div className="flex gap-4 bg-background-100 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">Spreadsheet öffnen</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">Öffne das OOPBUY Spreadsheet über die Hauptwebsite und wähle deine gewünschte Kategorie.</p>
                </div>
              </div>

              <div className="flex gap-4 bg-background-100 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">Produkt auswählen</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">Durchsuche die Liste nach deinem Wunschprodukt mit Verkäuferinfos und Preisangaben.</p>
                </div>
              </div>

              <div className="flex gap-4 bg-background-100 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">QC-Bilder prüfen</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    Überprüfe die Qualitätskontrollbilder. Tipps dazu findest du im{' '}
                    <button onClick={() => { navigate('/artikel/oopbuy-qc-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 underline cursor-pointer">QC Guide</button>.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 bg-background-100 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">Bestellung aufgeben</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">Kopiere den Link und gib deine Bestellung über OOPBUY auf.</p>
                </div>
              </div>
            </div>
          </section>

          <section id="section-5" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">5. OOPBUY Spreadsheet vs. andere Plattformen</h2>
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-secondary-200">
                      <th className="text-left py-3 pr-4 font-medium text-foreground-900">Feature</th>
                      <th className="text-left py-3 pr-4 font-medium text-accent-600">OOPBUY Spreadsheet</th>
                      <th className="text-left py-3 pr-4 font-medium text-foreground-500">Andere Plattformen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[['Produktdaten', 'Detailliert & geprüft', 'Oft unvollständig'],['Preisübersicht', 'Direkter Vergleich', 'Manuelle Suche'],['QC-Bilder', 'Bereits enthalten', 'Müssen angefragt werden'],['Aktualisierung', 'Regelmäßig', 'Selten'],['Benutzerfreundlichkeit', 'Einsteigerfreundlich', 'Oft komplex']].map((row, i) => (
                      <tr key={i} className="border-b border-secondary-100"><td className="py-3 pr-4 font-medium text-foreground-800">{row[0]}</td><td className="py-3 pr-4 text-accent-700">{row[1]}</td><td className="py-3 pr-4 text-foreground-500">{row[2]}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>Für einen detaillierten Vergleich aller Shopping-Agenten, lies unseren <button onClick={() => { navigate('/artikel/oopbuy-vs-alternatives'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 hover:text-accent-600 underline cursor-pointer">OOPBUY vs Alternatives Vergleich</button>.</p>
            </div>
          </section>

          <section id="section-6" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">6. Tipps für den optimalen Einkauf</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm text-foreground-700">
              <li><strong>QC-Bilder immer prüfen.</strong> Siehe <button onClick={() => { navigate('/artikel/oopbuy-qc-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 underline cursor-pointer">QC Guide</button>.</li>
              <li><strong>Versandkosten kalkulieren.</strong> Infos im <button onClick={() => { navigate('/artikel/oopbuy-shipping-deutschland'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 underline cursor-pointer">Shipping Guide</button>.</li>
              <li><strong>Community-Feedback beachten.</strong> Bewertungen sind Gold wert.</li>
              <li><strong>Größentabellen vergleichen.</strong> Asiatische Größen fallen kleiner aus.</li>
              <li><strong>Regelmäßig vorbeischauen.</strong> Neue Deals erscheinen fast täglich.</li>
            </ul>
          </section>

          <section id="section-7" className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">7. Häufig gestellte Fragen (FAQ)</h2>
            <div className="space-y-3">
              <details className="bg-background-100 rounded-xl group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                  Was ist das OOPBUY Spreadsheet genau?
                  <i className="ri-add-line text-foreground-400 group-open:hidden" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">Eine umfassende, kuratierte Übersichtstabelle mit tausenden geprüften Produkten aus chinesischen Online-Marktplätzen.</div>
              </details>

              <details className="bg-background-100 rounded-xl group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                  Ist das OOPBUY Spreadsheet kostenlos?
                  <i className="ri-add-line text-foreground-400 group-open:hidden" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">Ja, vollständig kostenlos nutzbar – keine versteckten Kosten oder Abonnements.</div>
              </details>

              <details className="bg-background-100 rounded-xl group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                  Kann ich Produkte direkt kaufen?
                  <i className="ri-add-line text-foreground-400 group-open:hidden" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">Nein, das Spreadsheet ist eine reine Informationsplattform. Der Kauf erfolgt über OOPBUY oder andere Agenten.</div>
              </details>

              <details className="bg-background-100 rounded-xl group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                  Gibt es Versand nach Deutschland?
                  <i className="ri-add-line text-foreground-400 group-open:hidden" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  Ja! Lies unseren{' '}
                  <button onClick={() => { navigate('/artikel/oopbuy-shipping-deutschland'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 underline cursor-pointer">Shipping Deutschland Guide</button>{' '}
                  für alle Details.
                </div>
              </details>

              <details className="bg-background-100 rounded-xl group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                  Ist OOPBUY sicher?
                  <i className="ri-add-line text-foreground-400 group-open:hidden" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  Ja, OOPBUY ist ein etablierter Agent. Mehr dazu im{' '}
                  <button onClick={() => { navigate('/artikel/oopbuy-sicher-einkaufen'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-accent-500 underline cursor-pointer">Sicherheits-Guide</button>.
                </div>
              </details>
            </div>
          </section>

          {/* Internal links */}
          <div className="mt-16 bg-background-100 rounded-2xl p-8">
            <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">Weitere hilfreiche Artikel</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {articleQuickLinks.filter((l) => l.path !== '/artikel/oopbuy-spreadsheet-guide').slice(0, 6).map((link) => (
                <button key={link.path} onClick={() => { navigate(link.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2 text-left px-4 py-3 rounded-xl bg-background-50 hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-circle-line text-accent-500 text-lg flex-shrink-0" />
                  <span className="text-sm text-foreground-700">{link.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-10 text-center mt-10">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">Bereit, das OOPBUY Spreadsheet zu entdecken?</h3>
            <p className="text-secondary-400 text-sm mb-6 max-w-md mx-auto">Starte jetzt und finde die besten Produkte zu den günstigsten Preisen.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="bg-white hover:bg-secondary-100 text-foreground-950 px-7 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap">oopbuy spreadsheet</a>
              <a href="/" className="border border-white/30 hover:border-white/60 text-white px-7 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap">Zurück zur Startseite</a>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-foreground-950 mt-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-secondary-500 text-xs">&copy; {new Date().getFullYear()} oopbuyspread.top – Eine unabhängige Informationsplattform</p>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <a href="/" className="text-secondary-500 hover:text-white text-xs transition-colors whitespace-nowrap">Startseite</a>
              {articleQuickLinks.slice(0, 4).map((link) => (
                <button key={link.path} onClick={() => { navigate(link.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-secondary-500 hover:text-white text-xs transition-colors cursor-pointer whitespace-nowrap">{link.label}</button>
              ))}
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-secondary-500 hover:text-white text-xs transition-colors whitespace-nowrap">oopbuy spreadsheet</a>
            </div>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: FAQ_SCHEMA }} />
    </main>
  );
}